import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Select from '../ui/Select';
import CloseButton from '../ui/CloseButton';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { 
  CheckCircle2, 
  AlertCircle, 
  Upload, 
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Loader2,
  FileText,
  Check,
  X,
  ClipboardPaste
} from 'lucide-react';
import { 
  parseCSV, 
  parseTabData, 
  validateImportData, 
  suggestColumnMapping,
  formatValidationErrors,
  ImportTable,
  ImportPreview,
  ImportResult
} from '../../utils/importUtils';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (data: any[]) => Promise<ImportResult>;
  table: ImportTable;
  initialData?: string;
}

const ImportModal: React.FC<ImportModalProps> = ({ 
  isOpen, 
  onClose, 
  onImport, 
  table,
  initialData
}) => {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload');
  const [rawData, setRawData] = useState('');
  const [parsedData, setParsedData] = useState<any>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadMethod, setUploadMethod] = useState<'csv' | 'paste'>('csv');
  const [isDragOver, setIsDragOver] = useState(false);
  const [dateFormat, setDateFormat] = useState<'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD'>('DD/MM/YYYY');
  const [fileName, setFileName] = useState<string>('');

  const steps = [
    { id: 'upload', label: t('import.steps.upload') || 'Fichier', number: 1 },
    { id: 'mapping', label: t('import.steps.mapping') || 'Colonnes', number: 2 },
    { id: 'preview', label: t('import.steps.preview') || 'Import', number: 3 },
  ];

  const currentStepIndex = steps.findIndex(s => s.id === step);

  // Reset modal when opened
  useEffect(() => {
    if (isOpen) {
      setStep('upload');
      setRawData(initialData || '');
      setParsedData(null);
      setColumnMapping({});
      setPreview(null);
      setImportResult(null);
      setIsLoading(false);
      setFileName('');
    }
  }, [isOpen, initialData]);

  // Handle file upload
  const handleFileUpload = (file: File) => {
    if (!file) return;
    setFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        handleDataParse(content, file.name);
      }
    };
    reader.readAsText(file);
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const file = files[0];
    
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv') || file.name.endsWith('.tsv'))) {
      handleFileUpload(file);
    } else {
      showToast(t('import.validation.invalidFile'), 'error');
    }
  };

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  // Handle data parsing
  const handleDataParse = (data: string, fileName?: string) => {
    setRawData(data);
    
    let parsed;
    const isTSV = fileName?.endsWith('.tsv') || (uploadMethod === 'paste' && data.includes('\t'));
    
    if (isTSV) {
      parsed = parseTabData(data);
    } else {
      parsed = parseCSV(data);
    }
    
    setParsedData(parsed);
    
    if (parsed.totalRows > 0) {
      const suggestions = suggestColumnMapping(parsed.headers, table.columns);
      setColumnMapping(suggestions);
      setStep('mapping');
    }
  };

  // Handle column mapping change
  const handleColumnMappingChange = (csvColumn: string, dbColumn: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [csvColumn]: dbColumn
    }));
  };

  // Validate and preview data
  const handlePreview = () => {
    if (!parsedData) return;
    
    const validation = validateImportData(parsedData, table, columnMapping, dateFormat);
    setPreview(validation);
    setStep('preview');
  };

  // Execute import
  const handleImport = async () => {
    if (!preview) return;
    
    setIsLoading(true);
    try {
      const result = await onImport(preview.data);
      setImportResult(result);

      // If quota limit reached, the Quota modal will open via QuotaContext.
      // Close the import modal immediately to avoid stacking modals / "stuck" UX.
      if (result?.errors?.includes('QUOTA_LIMIT')) {
        onClose();
        return;
      }
      
      if (result.success > 0) {
        showToast(
          t('import.success', { count: result.success }) || `${result.success} éléments importés avec succès`,
          'success'
        );
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (error) {
      setImportResult({
        success: 0,
        errors: [t('import.preview.importError')],
        warnings: []
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Get translated label for a column
  const getColumnLabel = (col: { labelKey: string; name: string }) => {
    return t(col.labelKey) || col.name;
  };

  // Get column options for mapping (using translated labels)
  const getColumnOptions = () => [
    { value: '', label: t('import.mapping.ignoreColumn') || '— Ignorer —' },
    ...table.columns.map(col => ({
      value: col.name,
      label: `${getColumnLabel(col)}${col.required ? ' *' : ''}`
    }))
  ];


  // Get unique values for a column (preview)
  const getUniqueValues = (csvColumn: string) => {
    if (!parsedData) return [];
    
    const columnIndex = parsedData.headers.indexOf(csvColumn);
    if (columnIndex === -1) return [];
    
    const values = new Set<string>();
    parsedData.rows.forEach((row: string[]) => {
      const value = row[columnIndex]?.trim();
      if (value) values.add(value);
    });
    
    return Array.from(values).slice(0, 3);
  };

  // Check if mapping is valid (at least one required field mapped)
  const isMappingValid = () => {
    const requiredColumns = table.columns.filter(c => c.required).map(c => c.name);
    const mappedColumns = Object.values(columnMapping).filter(v => v);
    return requiredColumns.some(req => mappedColumns.includes(req));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
      bodyClassName="!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !p-0"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 pb-5 pt-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/25">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('import.title') || 'Importer des données'}
              </h2>
              <p className="text-sm text-gray-500">
                {table.displayName}
              </p>
            </div>
          </div>
          <CloseButton onClick={onClose} />
        </div>

        {/* Stepper */}
        <div className="shrink-0 border-b border-gray-100 px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            {steps.map((s, index) => (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-2.5">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300
                    ${currentStepIndex > index 
                      ? 'bg-emerald-500 text-white' 
                      : currentStepIndex === index 
                        ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30' 
                        : 'bg-gray-100 text-gray-400'
                    }
                  `}>
                    {currentStepIndex > index ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      s.number
                    )}
                  </div>
                  <span className={`text-sm font-medium transition-colors ${
                    currentStepIndex >= index ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {s.label}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 rounded-full transition-colors duration-300 ${
                    currentStepIndex > index ? 'bg-emerald-500' : 'bg-gray-100'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-6">
          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="space-y-5">
              {/* Method Toggle */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-xl w-fit">
                <button
                  onClick={() => setUploadMethod('csv')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    uploadMethod === 'csv'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Upload className="w-4 h-4" />
                  {t('import.upload.csvTab') || 'Fichier CSV'}
                </button>
                <button
                  onClick={() => setUploadMethod('paste')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    uploadMethod === 'paste'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <ClipboardPaste className="w-4 h-4" />
                  {t('import.upload.pasteTab') || 'Coller'}
                </button>
              </div>

              {/* CSV Upload */}
              {uploadMethod === 'csv' && (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`
                    relative border-2 border-dashed rounded-2xl p-10 text-center transition-all duration-300 cursor-pointer
                    ${isDragOver 
                      ? 'border-primary-500 bg-primary-50/50 scale-[1.01]' 
                      : fileName 
                        ? 'border-emerald-300 bg-emerald-50/50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                    }
                  `}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <input
                    type="file"
                    accept=".csv,.tsv,text/csv"
                    onChange={handleFileInputChange}
                    className="hidden"
                    id="file-upload"
                  />
                  
                  {fileName ? (
                    <div className="flex flex-col items-center">
                      <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                        <FileText className="w-7 h-7 text-emerald-600" />
                      </div>
                      <p className="text-base font-semibold text-gray-900 mb-1">{fileName}</p>
                      <p className="text-sm text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4" />
                        {t('import.upload.fileReady') || 'Fichier prêt'}
                      </p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-colors ${
                        isDragOver ? 'bg-primary-100' : 'bg-gray-100'
                      }`}>
                        <Upload className={`w-7 h-7 transition-colors ${
                          isDragOver ? 'text-primary-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <p className="text-base font-semibold text-gray-900 mb-1">
                        {t('import.upload.dragDrop') || 'Glisse-dépose ton fichier'}
                      </p>
                      <p className="text-sm text-gray-500 mb-4">
                        {t('import.upload.or') || 'ou'}{' '}
                        <span className="text-primary-600 font-medium">
                          {t('import.upload.browse') || 'parcours tes fichiers'}
                        </span>
                      </p>
                      <p className="text-xs text-gray-400">
                        CSV, TSV • {t('import.upload.maxSize') || 'Taille max: 10MB'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Paste Data */}
              {uploadMethod === 'paste' && (
                <div className="space-y-3">
                  <textarea
                    value={rawData}
                    onChange={(e) => setRawData(e.target.value)}
                    placeholder={t('import.upload.pastePlaceholder') || 'Colle tes données ici...\n\nLa première ligne doit contenir les en-têtes.\nSéparateur : tabulation ou virgule.'}
                    className="w-full h-56 p-4 text-base sm:text-sm font-mono bg-gray-50 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all placeholder:text-gray-400"
                  />
                </div>
              )}
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 'mapping' && parsedData && (
            <div className="space-y-5">
              {/* Stats */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('import.mapping.rows') || 'Lignes'}</p>
                    <p className="text-sm font-semibold text-gray-900">{parsedData.totalRows}</p>
                  </div>
                </div>
                <div className="w-px h-8 bg-gray-200" />
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('import.mapping.columns') || 'Colonnes'}</p>
                    <p className="text-sm font-semibold text-gray-900">{parsedData.headers.length}</p>
                  </div>
                </div>
              </div>

              {/* Date format (if date columns detected) */}
              {parsedData.headers.some((header: string) => 
                columnMapping[header] && 
                ['last_action_at', 'next_action_at', 'created_at', 'updated_at'].includes(columnMapping[header])
              ) && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <p className="text-sm font-medium text-amber-800 mb-3">
                    {t('import.mapping.dateFormatTitle') || 'Format des dates dans ton fichier'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'DD/MM/YYYY', label: 'JJ/MM/AAAA', example: '25/12/2024' },
                      { value: 'MM/DD/YYYY', label: 'MM/JJ/AAAA', example: '12/25/2024' },
                      { value: 'YYYY-MM-DD', label: 'AAAA-MM-JJ', example: '2024-12-25' },
                    ].map(format => (
                      <button
                        key={format.value}
                        onClick={() => setDateFormat(format.value as any)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          dateFormat === format.value
                            ? 'bg-amber-200 text-amber-900'
                            : 'bg-white text-amber-700 hover:bg-amber-100'
                        }`}
                      >
                        {format.example}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Column Mapping */}
              <div className="space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  {t('import.mapping.matchColumns') || 'Associe les colonnes de ton fichier'}
                </p>
                <div className="space-y-2">
                  {parsedData.headers
                    .filter((header: string) => header && header.trim() !== '')
                    .map((header: string) => {
                      const mappedTo = columnMapping[header];
                      const examples = getUniqueValues(header);
                      
                      return (
                        <div 
                          key={header}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                        >
                          {/* Source column */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{header}</p>
                            {examples.length > 0 && (
                              <p className="text-xs text-gray-400 truncate">
                                {examples.join(', ')}{examples.length >= 3 && '...'}
                              </p>
                            )}
                          </div>
                          
                          {/* Arrow */}
                          <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          
                          {/* Target column select */}
                          <div className="w-48 flex-shrink-0">
                            <Select
                              value={mappedTo || ''}
                              onChange={(value) => handleColumnMappingChange(header, value)}
                              options={getColumnOptions()}
                            />
                          </div>
                          
                          {/* Status indicator */}
                          <div className="w-6 flex-shrink-0">
                            {mappedTo ? (
                              <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-emerald-600" />
                              </div>
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                                <X className="w-3.5 h-3.5 text-gray-400" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Preview & Import */}
          {step === 'preview' && preview && (
            <div className="space-y-5">
              {/* Validation Results */}
              {preview.errors.length > 0 && (
                <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-red-800 mb-1">
                        {t('import.preview.errorsFound') || 'Erreurs détectées'}
                      </p>
                      <p className="text-sm text-red-700">
                        {formatValidationErrors(preview.errors)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {preview.warnings.length > 0 && (
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-amber-800 mb-1">
                        {t('import.preview.warnings') || 'Avertissements'}
                      </p>
                      <p className="text-sm text-amber-700">{preview.warnings.join('; ')}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success state after import */}
              {importResult && importResult.success > 0 && (
                <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-100 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </div>
                  <p className="text-lg font-semibold text-emerald-800">
                    {importResult.success} {t('import.preview.rowsImported') || 'éléments importés'}
                  </p>
                </div>
              )}

              {/* Preview Table */}
              {!importResult && preview.data.length > 0 && (
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {t('import.preview.tableTitle') || 'Aperçu des données'}
                    </span>
                    <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded-md">
                      {preview.data.length} {t('import.preview.rows') || 'lignes'}
                    </span>
                  </div>
                  <div className="max-h-56 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {table.columns.filter(col => 
                            Object.values(columnMapping).includes(col.name)
                          ).map((column) => (
                            <th 
                              key={column.name} 
                              className="px-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
                            >
                              {getColumnLabel(column)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {preview.data.slice(0, 10).map((row, index) => (
                          <tr key={index} className="hover:bg-gray-50/50">
                            {table.columns.filter(col => 
                              Object.values(columnMapping).includes(col.name)
                            ).map((column) => {
                              const value = row[column.name];
                              return (
                                <td 
                                  key={column.name} 
                                  className="px-4 py-2.5 text-gray-700 whitespace-nowrap max-w-[200px] truncate"
                                >
                                  {value === null || value === undefined ? (
                                    <span className="text-gray-300">—</span>
                                  ) : typeof value === 'object' && value instanceof Date ? (
                                    isNaN(value.getTime()) ? (
                                      <span className="text-red-400">Invalid</span>
                                    ) : (
                                      value.toLocaleDateString()
                                    )
                                  ) : (
                                    String(value)
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {preview.data.length > 10 && (
                    <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 text-center border-t border-gray-100">
                      +{preview.data.length - 10} {t('import.preview.moreRows') || 'autres lignes'}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-between border-t border-gray-100 px-4 py-4 sm:px-6 sm:py-5">
          <div>
            {step !== 'upload' && !importResult && (
              <Button
                onClick={() => setStep(step === 'preview' ? 'mapping' : 'upload')}
                variant="outline"
                disabled={isLoading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('import.buttons.previous') || 'Retour'}
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {step === 'upload' && (
              <Button
                onClick={() => handleDataParse(rawData)}
                disabled={!rawData.trim() && !fileName}
                variant="primary"
              >
                {t('import.buttons.continue') || 'Continuer'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            
            {step === 'mapping' && (
              <Button
                onClick={handlePreview}
                disabled={!isMappingValid()}
                variant="primary"
              >
                {t('import.buttons.preview') || 'Prévisualiser'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            
            {step === 'preview' && !importResult && (
              <Button
                onClick={handleImport}
                disabled={(preview?.errors?.length ?? 0) > 0 || isLoading}
                variant="primary"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('import.buttons.importing') || 'Import en cours...'}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {t('import.buttons.import') || 'Importer'} {preview?.totalRows} {t('import.preview.rows') || 'lignes'}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImportModal;
